import React from 'react';
import styled from 'styled-components';
import { Github, Linkedin, Mail, Heart } from 'lucide-react';
import { theme } from '../../styles/theme';

const FooterContainer = styled.footer`
  background-color: ${theme.colors.backgroundAlt};
  border-top: 1px solid ${theme.colors.border};
  padding: ${theme.spacing['2xl']} 0 ${theme.spacing.xl};
  margin-top: auto;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing['2xl']};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const FooterTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

const FooterText = styled.p`
  color: ${theme.colors.textLight};
  line-height: 1.6;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.md}) {
    justify-content: center;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.lg};
  background-color: ${theme.colors.surface};
  color: ${theme.colors.textLight};
  transition: all 0.15s ease;
  border: 1px solid ${theme.colors.border};

  &:hover {
    background-color: ${theme.colors.primary};
    color: white;
    transform: translateY(-2px);
  }
`;

const QuickLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const QuickLink = styled.a`
  color: ${theme.colors.textLight};
  transition: color 0.2s ease;

  &:hover {
    color: ${theme.colors.primary};
  }
`;

const FooterBottom = styled.div`
  margin-top: ${theme.spacing['2xl']};
  padding-top: ${theme.spacing.xl};
  border-top: 1px solid ${theme.colors.border};
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const FooterBottomText = styled.p`
  color: ${theme.colors.textLight};
  font-size: ${theme.fontSizes.sm};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const HeartIcon = styled(Heart)`
  color: ${theme.colors.error};
  fill: ${theme.colors.error};
  width: 14px;
  height: 14px;
`;

export const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <FooterTitle>Antonio Michal Verdile</FooterTitle>
          <FooterText>
            Full Stack Developer specializing in modern web technologies. 
            Open to professional opportunities and technical collaborations.
          </FooterText>
          <SocialLinks>
            <SocialLink 
              href="https://github.com/MichalVerdile" 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label="GitHub"
            >
              <Github size={20} />
            </SocialLink>
            <SocialLink 
              href="https://linkedin.com/in/michal-verdile-61b813151" 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <Linkedin size={20} />
            </SocialLink>
            <SocialLink 
              href="mailto:michaelverdile1@gmail.com"
              aria-label="Email"
            >
              <Mail size={20} />
            </SocialLink>
          </SocialLinks>
        </FooterSection>

        <FooterSection>
          <FooterTitle>Navigation</FooterTitle>
          <QuickLinks>
            <QuickLink href="/">Home</QuickLink>
            <QuickLink href="/about">About</QuickLink>
            <QuickLink href="/projects">Projects</QuickLink>
            <QuickLink href="/contact">Contact</QuickLink>
          </QuickLinks>
        </FooterSection>

        <FooterSection>
          <FooterTitle>Featured Projects</FooterTitle>
          <QuickLinks>
            <QuickLink href="/projects">Lernello - AI E-Learning</QuickLink>
            <QuickLink href="/projects">Real Estate Price Predictor</QuickLink>
            <QuickLink href="/projects">New Living Design Website</QuickLink>
            <QuickLink href="/projects">View All Projects</QuickLink>
          </QuickLinks>
        </FooterSection>
      </FooterContent>

      <FooterContent>
        <FooterBottom>
          <FooterBottomText>
            © {currentYear} Antonio Michal Verdile. Made with <HeartIcon /> using React and TypeScript
          </FooterBottomText>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};